import React, { useState, useEffect, useRef } from "react";
import { Eye, EyeOff, User, Lock, Mail, X, ArrowLeft } from 'lucide-react';
import axios from "axios";
import { useNavigate } from "react-router-dom";

function Login() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const loginHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    try {
      const { data } = await axios.post(
        "https://portal-sanieldanproperties-api.onrender.com/auth/login",
        { username, password },
        config
      );

      localStorage.setItem("authToken", data.Token);
      localStorage.setItem("authUser", JSON.stringify(data.user));
      if (rememberMe) {
        localStorage.setItem("rememberedUsername", username);
      }

      setSuccess("Login successful! Redirecting to dashboard...");
      setUsername("");
      setPassword("");
      setTimeout(() => {
        setSuccess("");
        setLoading(false);
        navigate("/dashboard");
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.error || "Login failed. Please try again.");
      setPassword("");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("authUser")) {
      navigate("/dashboard");
    }
    const rememberedUsername = localStorage.getItem("rememberedUsername");
    if (rememberedUsername) {
      setUsername(rememberedUsername);
      setRememberMe(true);
    }
  }, [navigate]);

  if (showForgotPassword) {
    return <ForgotPasswordComponent onBack={() => setShowForgotPassword(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-4xl font-bold inline">Welcome </h1>
          <h1 className="text-4xl font-bold text-amber-600 inline">Back</h1>
          <div className="mt-3 flex justify-center">
            <div className="h-1 w-32 bg-amber-600 rounded"></div>
          </div>
          <p className="mt-4 text-gray-600 text-sm px-4">
            Welcome to our portal! Please enter your login credentials to access your account.
            {' '}
            <a href="/signup" className="text-amber-600 hover:text-amber-700 font-medium">
              Sign up here
            </a>
            {' '}
            if you don't have an account.
          </p>
        </div>
      </div>

      {(error || success) && (
        <div className="fixed top-4 right-4 max-w-sm z-50 animate-slideIn">
          <div className={`rounded-lg shadow-lg px-4 py-3 ${error ? 'bg-red-50 border-l-4 border-red-500' : 'bg-emerald-50 border-l-4 border-emerald-500'}`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {error ? (
                  <span className="text-red-500">⚠️</span>
                ) : (
                  <span className="text-emerald-500">✓</span>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${error ? 'text-red-800' : 'text-emerald-800'}`}>
                  {error || success}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={loginHandler}>
            <div>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-amber-500" />
                </div>
                <input
                  type="text"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                  placeholder="Username"
                />
              </div>
            </div>

            <div>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-amber-500" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                  placeholder="Password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-amber-600 hover:text-amber-700 font-medium"
              >
                Forgot password?
              </button>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-600 transition-colors duration-200 disabled:opacity-50"
              >
                {loading ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  "Sign in"
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Don't have an account?{' '}
                  <a href="/signup" className="font-medium text-amber-600 hover:text-amber-700">
                    Sign up
                  </a>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ForgotPasswordComponent({ onBack }) {
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [step, setStep] = useState("email"); // email, code, password
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const emailInputRef = useRef(null);
  const codeInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  useEffect(() => {
    if (step === "email" && emailInputRef.current) {
      emailInputRef.current.focus();
    } else if (step === "code" && codeInputRef.current) {
      codeInputRef.current.focus();
    } else if (step === "password" && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [step]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    clearMessages();
    setLoading(true);

    try {
      await axios.post(
        "https://portal-sanieldanproperties-api.onrender.com/auth/forgot-password",
        { email }
      );

      setSuccess("Reset code sent to your email!");
      setStep("code");
      startCountdown();
    } catch (error) {
      setError(error.response?.data?.error || "Failed to send reset code");
    }
    setLoading(false);
  };

  const handleVerifyCode = async () => {
    if (!resetCode) {
      setError("Please enter the reset code");
      return;
    }

    clearMessages();
    setLoading(true);

    try {
      await axios.post(
        "https://portal-sanieldanproperties-api.onrender.com/auth/verify-reset-code",
        { email, resetCode }
      );

      setSuccess("Code verified successfully!");
      setStep("password");
    } catch (error) {
      setError(error.response?.data?.error || "Invalid reset code");
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmNewPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    clearMessages();
    setLoading(true);

    try {
      await axios.post(
        "https://portal-sanieldanproperties-api.onrender.com/auth/reset-password",
        { email, resetCode, newPassword }
      );

      setSuccess("Password reset successfully! Redirecting to login...");
      setTimeout(() => {
        onBack();
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.error || "Failed to reset password");
    }
    setLoading(false);
  };

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;

    clearMessages();
    setLoading(true);

    try {
      await axios.post(
        "https://portal-sanieldanproperties-api.onrender.com/auth/forgot-password",
        { email }
      );

      setSuccess("Reset code sent again!");
      startCountdown();
    } catch (error) {
      setError(error.response?.data?.error || "Failed to resend code");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-amber-600">Reset Password</h1>
          <div className="mt-3 flex justify-center">
            <div className="h-1 w-32 bg-amber-600 rounded"></div>
          </div>
        </div>
      </div>

      {(error || success) && (
        <div className="fixed top-4 right-4 max-w-sm z-50 animate-slideIn">
          <div className={`rounded-lg shadow-lg px-4 py-3 ${error ? 'bg-red-50 border-l-4 border-red-500' : 'bg-emerald-50 border-l-4 border-emerald-500'}`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {error ? (
                  <span className="text-red-500">⚠️</span>
                ) : (
                  <span className="text-emerald-500">✓</span>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${error ? 'text-red-800' : 'text-emerald-800'}`}>
                  {error || success}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg rounded-lg sm:px-10">
          {step === "email" && (
            <div className="space-y-6">
              <p className="text-gray-600 text-sm">
                Enter your email address and we'll send you a verification code to reset your password.
              </p>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  ref={emailInputRef}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                  placeholder="Enter your email address"
                />
              </div>
              <button
                onClick={handleForgotPassword}
                disabled={loading || !email}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-600 disabled:opacity-50 transition-colors duration-200"
              >
                {loading ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  "Send Reset Code"
                )}
              </button>
              <button
                onClick={onBack}
                className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-600 transition-colors duration-200"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to Login
              </button>
            </div>
          )}

          {step === "code" && (
            <div className="space-y-6">
              <p className="text-gray-600 text-sm">
                We've sent a 6-digit verification code to <strong>{email}</strong>. Please enter it below.
              </p>
              <input
                ref={codeInputRef}
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="block w-full px-3 py-3 border border-gray-300 rounded-md text-center text-xl font-mono tracking-widest focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                placeholder="000000"
                maxLength={6}
              />
              <button
                onClick={handleVerifyCode}
                disabled={loading || resetCode.length !== 6}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-600 disabled:opacity-50 transition-colors duration-200"
              >
                {loading ? "Verifying..." : "Verify Code"}
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={countdown > 0 || loading}
                className="w-full text-sm text-amber-600 hover:text-amber-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {countdown > 0 ? `Resend in ${countdown}s` : "Resend Code"}
              </button>
              <button
                onClick={() => setStep("email")}
                className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-600 transition-colors duration-200"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back
              </button>
            </div>
          )}

          {step === "password" && (
            <div className="space-y-6">
              <p className="text-gray-600 text-left text-sm">
                Please enter your new password below.
              </p>
              <div>
                <label className="block text-left text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  ref={passwordInputRef}
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                  placeholder="Confirm new password"
                />
              </div>
              <button
                onClick={handleResetPassword}
                disabled={loading || !newPassword || !confirmNewPassword}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-600 disabled:opacity-50 transition-colors duration-200"
              >
                {loading ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  "Reset Password"
                )}
              </button>
              <button
                onClick={() => setStep("code")}
                className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-600 transition-colors duration-200"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;